import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Text, 
  NumberInput, 
  Button, 
  Group, 
  Stack, 
  Alert,
  Divider,
  Image,
  Tooltip,
  Box,
  Select,
  Checkbox,
  ScrollArea
} from '@mantine/core';
import { 
  IconAlertCircle, 
  IconCircleCheck, 
  IconCircleX, 
  IconInfoCircle,
  IconSword,
  IconShirt,
  IconBook,
  IconHorseToy,
  IconDiamond,
  IconMask,
  IconScissors,
  IconSwords,
  IconShield,
  IconArmchair,
  IconHelmet,
  IconSock,
  IconShoe,
  IconManualGearbox,
  IconWind,
  IconBriefcase,
  IconSkull,
  IconPaw
} from '@tabler/icons-react';
import { Item, Gem, addItem, removeItem, getGems } from '../api/items';
import { useLanguageContext } from '../contexts/LanguageContext';
import { Character } from '../api/characters';
import { notifications } from '@mantine/notifications';

interface ItemPropertiesProps {
  selectedItem: Item | null;
  selectedCharacter: Character | null;
  serverConnected: boolean;
}

/**
 * Item Properties component
 * Displays item properties and allows giving/removing items
 */
const ItemProperties: React.FC<ItemPropertiesProps> = ({ 
  selectedItem, 
  selectedCharacter,
  serverConnected
}) => {
  const { language } = useLanguageContext();
  // State for item properties
  const [quantity, setQuantity] = useState<number>(1);
  const [level, setLevel] = useState<number>(1);
  const [enchant, setEnchant] = useState<number>(-1);
  const [tamed, setTamed] = useState<boolean>(false);
  const [socket1, setSocket1] = useState<number | null>(null);
  const [socket2, setSocket2] = useState<number | null>(null);
  const [socket3, setSocket3] = useState<number | null>(null);
  const [socket4, setSocket4] = useState<number | null>(null);
  const [beltSocketCount, setBeltSocketCount] = useState<number>(1);
  
  // State for gem options
  const [gems, setGems] = useState<{ value: string; label: string; tooltip: string; icon: string }[]>([]);
  const [isLoadingGems, setIsLoadingGems] = useState(false);
  
  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check if buttons should be disabled
  const isButtonsDisabled = !serverConnected || !selectedItem || !selectedCharacter || isSubmitting;

  // Load gems when component mounts
  useEffect(() => {
    const fetchGems = async () => {
      if (!selectedItem || selectedItem.socket <= 0) return;
      
      setIsLoadingGems(true);
      try {
        const response = await getGems(language);
        // Format gems for select component
        const gemOptions = response.gems.map(gem => ({
          value: gem.id.toString(),
          label: gem.name,
          tooltip: gem.tooltip,
          icon: gem.icon_file_name
        }));
        
        // Add empty option
        gemOptions.unshift({ value: '0', label: 'No Gem', tooltip: '', icon: '' });
        
        setGems(gemOptions);
      } catch (err) {
        console.error('Failed to fetch gems:', err);
        setError('Failed to load gems. Socket selection may not work properly.');
      } finally {
        setIsLoadingGems(false);
      }
    };
    
    fetchGems();
  }, [selectedItem, language]);
  
  // Reset form when selected item changes
  useEffect(() => {
    if (selectedItem) {
      setQuantity(1);
      setLevel(1);
      setEnchant(-1);
      setTamed(false);
      setSocket1(null);
      setSocket2(null);
      setSocket3(null);
      setSocket4(null);
      setBeltSocketCount(1);
    }
  }, [selectedItem]);
  
  // Get the icon URL for an item
  const getItemIconUrl = (iconFileName: string) => {
    return `/icons/items/${iconFileName}.jpg`;
  };
  
  // Determine the item type for display and validation
  const getItemType = (item: Item): string => {
    // Drop pet check (wear_type=501, group=18)
    if (item.class === 501 && item.group === 18) {
      return 'DROP_PET';
    }
    
    // Not wearable
    if (item.wear_type === -1) {
      // Pet card check (wear_type=-1, group=13, type=2)
      if (item.group === 13 && item.type === 2) {
        return 'PET_CARD';
      }
      return 'NOT_WEARABLE';
    }
    
    // ARMOR: Armor (2), Helmet (3), Glove (4), Boots (5), Belt (6), Cloak (7), Necklace (8), Ring (9), Earring (11)
    if ([2, 3, 4, 5, 6, 7, 8, 9, 11].includes(item.wear_type)) {
      return 'ARMOR';
    }
    
    // WEAPON: 1h weapon (0), 2h weapon (99), Shield (1)
    if ([0, 1, 99].includes(item.wear_type)) {
      return 'WEAPON';
    }
    
    // Skill card (100)
    if (item.wear_type === 100) {
      return 'SKILL_CARD';
    }
    
    // Mount (22)
    if (item.wear_type === 22) {
      return 'MOUNT';
    }
    
    // Creature artifacts (200)
    if (item.wear_type === 200) {
      return 'CREATURE_ARTIFACT';
    }
    
    // Mask (12)
    if (item.wear_type === 12) {
      return 'MASK';
    }
    
    // Hairstyle (13)
    if (item.wear_type === 13) {
      return 'HAIRSTYLE';
    }
    
    // Decorated items
    if (item.wear_type === 14) return 'DECORATED_WEAPON';
    if (item.wear_type === 15) return 'DECORATED_SHIELD';
    if (item.wear_type === 16) return 'DECORATED_ARMOR';
    if (item.wear_type === 17) return 'DECORATED_HAT';
    if (item.wear_type === 18) return 'DECORATED_GLOVES';
    if (item.wear_type === 19) return 'DECORATED_BOOTS';
    if (item.wear_type === 20) return 'DECORATED_CLOAK';
    
    // Wings (21)
    if (item.wear_type === 21) {
      return 'WINGS';
    }
    
    // Bag (23)
    if (item.wear_type === 23) {
      return 'BAG';
    }
    
    // Boss card (198)
    if (item.wear_type === 198) {
      return 'BOSS_CARD';
    }
    
    return 'MISC';
  };
  
  // Determine item type icon based on type
  const getItemTypeIcon = (itemType: string) => {
    switch (itemType) {
      case 'WEAPON':
        return <IconSword size={14} color="blue" stroke={1.5} />;
      case 'ARMOR':
        return <IconShirt size={14} color="green" stroke={1.5} />;
      case 'SKILL_CARD':
        return <IconBook size={14} color="purple" stroke={1.5} />;
      case 'PET_CARD':
        return <IconPaw size={14} color="orange" stroke={1.5} />;
      case 'DROP_PET':
        return <IconPaw size={14} color="purple" stroke={1.5} />;
      case 'NOT_WEARABLE':
        return <IconBook size={14} color="gray" stroke={1.5} />;
      case 'MOUNT':
        return <IconHorseToy size={14} color="brown" stroke={1.5} />;
      case 'CREATURE_ARTIFACT':
        return <IconDiamond size={14} color="indigo" stroke={1.5} />;
      case 'MASK':
        return <IconMask size={14} color="teal" stroke={1.5} />;
      case 'HAIRSTYLE':
        return <IconScissors size={14} color="pink" stroke={1.5} />;
      case 'DECORATED_WEAPON':
        return <IconSwords size={14} color="cyan" stroke={1.5} />;
      case 'DECORATED_SHIELD':
        return <IconShield size={14} color="lime" stroke={1.5} />;
      case 'DECORATED_ARMOR':
        return <IconArmchair size={14} color="violet" stroke={1.5} />;
      case 'DECORATED_HAT':
        return <IconHelmet size={14} color="grape" stroke={1.5} />;
      case 'DECORATED_GLOVES':
        return <IconSock size={14} color="red" stroke={1.5} />;
      case 'DECORATED_BOOTS':
        return <IconShoe size={14} color="yellow" stroke={1.5} />;
      case 'DECORATED_CLOAK':
        return <IconManualGearbox size={14} color="orange" stroke={1.5} />;
      case 'WINGS':
        return <IconWind size={14} color="blue" stroke={1.5} />;
      case 'BAG':
        return <IconBriefcase size={14} color="green" stroke={1.5} />;
      case 'BOSS_CARD':
        return <IconSkull size={14} color="red" stroke={1.5} />;
      default:
        return <IconBook size={14} color="gray" stroke={1.5} />;
    }
  };
  
  // Get type name for display
  const getItemTypeName = (itemType: string): string => {
    switch (itemType) {
      case 'WEAPON':
        return 'Weapon';
      case 'ARMOR':
        return 'Armor';
      case 'SKILL_CARD':
        return 'Skill Card';
      case 'PET_CARD':
        return 'Pet Card';
      case 'DROP_PET':
        return 'Drop Pet';
      case 'NOT_WEARABLE':
        return 'Not Wearable';
      case 'MOUNT':
        return 'Mount';
      case 'CREATURE_ARTIFACT':
        return 'Creature Artifact';
      case 'MASK':
        return 'Mask';
      case 'HAIRSTYLE':
        return 'Hairstyle';
      case 'DECORATED_WEAPON':
        return 'Decorated Weapon';
      case 'DECORATED_SHIELD':
        return 'Decorated Shield';
      case 'DECORATED_ARMOR':
        return 'Decorated Armor';
      case 'DECORATED_HAT':
        return 'Decorated Hat';
      case 'DECORATED_GLOVES':
        return 'Decorated Gloves';
      case 'DECORATED_BOOTS':
        return 'Decorated Boots';
      case 'DECORATED_CLOAK':
        return 'Decorated Cloak';
      case 'WINGS':
        return 'Wings';
      case 'BAG':
        return 'Bag';
      case 'BOSS_CARD':
        return 'Boss Card';
      default:
        return 'Miscellaneous';
    }
  };
  
  // Handle adding item
  const handleAddItem = async () => {
    if (isButtonsDisabled || !selectedItem || !selectedCharacter) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Determine item type for backend processing
      const itemType = getItemType(selectedItem);
      
      // For belts, use beltSocketCount to determine socket1 value
      let finalSocket1 = socket1;
      let finalSocket2 = socket2;
      let finalSocket3 = socket3;
      let finalSocket4 = socket4;
      
      // If item is a belt, use beltSocketCount as socket1 value and set others to 0
      if (selectedItem.wear_type === 6) {
        finalSocket1 = beltSocketCount;
        finalSocket2 = 0;
        finalSocket3 = 0;
        finalSocket4 = 0;
      }
      
      const result = await addItem({
        itemId: selectedItem.id,
        quantity,
        level,
        enchant: enchant >= 0 ? enchant : undefined,
        tamed: itemType === 'PET_CARD' ? tamed : undefined,
        socket1: finalSocket1 !== null ? Number(finalSocket1) : undefined,
        socket2: finalSocket2 !== null ? Number(finalSocket2) : undefined,
        socket3: finalSocket3 !== null ? Number(finalSocket3) : undefined,
        socket4: finalSocket4 !== null ? Number(finalSocket4) : undefined,
        itemType,
        characterId: selectedCharacter.id,
        characterName: selectedCharacter.name
      });
      
      notifications.show({
        title: 'Item Added',
        message: `Added ${quantity} ${selectedItem.value} to ${selectedCharacter.name}`,
        color: 'green',
        icon: <IconCircleCheck size={16} />
      });
    } catch (err) {
      console.error('Failed to add item:', err);
      setError('Failed to add item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle removing item
  const handleRemoveItem = async () => {
    if (isButtonsDisabled || !selectedItem || !selectedCharacter) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await removeItem({
        itemId: selectedItem.id,
        quantity,
        characterId: selectedCharacter.id,
        characterName: selectedCharacter.name
      });
      
      notifications.show({
        title: 'Item Removed',
        message: `Removed ${quantity} ${selectedItem.value} from ${selectedCharacter.name}`,
        color: 'yellow',
        icon: <IconCircleX size={16} />
      });
    } catch (err) {
      console.error('Failed to remove item:', err);
      setError('Failed to remove item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Render gem selection fields based on item sockets
  const renderGemSelections = () => {
    if (!selectedItem || selectedItem.socket <= 0) return null;
    
    // Special case for belts - they show a socket count field instead of gem selections
    if (selectedItem.wear_type === 6) {
      return (
        <Stack gap="xs" mt="md">
          <Divider label="Belt Properties" labelPosition="center" />
          <Box>
            <Text size="sm" mb={5} fw={500}>
              Socket count
            </Text>
            <NumberInput
              value={beltSocketCount}
              onChange={(value) => setBeltSocketCount(Number(value))}
              min={1}
              max={6}
              stepHoldDelay={500}
              stepHoldInterval={100}
              hideControls={false}
              required
              size="sm"
              placeholder="1-6"
            />
            <Text size="xs" c="dimmed" mt={5}>
              Number of inventory slots the belt provides (1-6)
            </Text>
          </Box>
        </Stack>
      );
    }
    
    // Standard socket display for non-belt items
    const sockets = [];
    
    for (let i = 1; i <= selectedItem.socket; i++) {
      let socketValue: number | null = null;
      let setSocketValue: React.Dispatch<React.SetStateAction<number | null>> = () => {};
      
      // Assign the correct state variable based on socket index
      switch (i) {
        case 1:
          socketValue = socket1;
          setSocketValue = setSocket1;
          break;
        case 2:
          socketValue = socket2;
          setSocketValue = setSocket2;
          break;
        case 3:
          socketValue = socket3;
          setSocketValue = setSocket3;
          break;
        case 4:
          socketValue = socket4;
          setSocketValue = setSocket4;
          break;
      }
      
      sockets.push(
        <Box key={`socket-${i}`}>
          <Text size="sm" mb={5} fw={500}>
            Socket {i}
          </Text>
          <Select
            placeholder={isLoadingGems ? "Loading gems..." : "Select gem"}
            value={socketValue !== null ? socketValue.toString() : null}
            onChange={(value) => setSocketValue(value ? Number(value) : null)}
            data={gems}
            clearable
            size="sm"
            disabled={isLoadingGems}
            searchable
            renderOption={({ option }) => {
              const optionWithIcon = option as { value: string; label: string; icon: string };
              return (
                <Group gap="xs" wrap="nowrap">
                  {optionWithIcon.icon && (
                    <Image
                      src={`/icons/items/${optionWithIcon.icon}.jpg`}
                      width={20}
                      height={20}
                      fit="contain"
                      fallbackSrc="/icons/fallback.jpg"
                      alt={String(optionWithIcon.label)}
                      className="rounded"
                    />
                  )}
                  <Text size="sm">{optionWithIcon.label}</Text>
                </Group>
              );
            }}
          />
        </Box>
      );
    }
    
    return (
      <Stack gap="xs" mt="md">
        <Divider label="Sockets" labelPosition="center" />
        {sockets}
      </Stack>
    );
  };
  
  return (
    <Paper shadow="xs" p="md" radius={0} h="100%" withBorder className="h-full flex flex-col">
      <Text fw={700} mb="md">Properties</Text>
      
      {selectedItem ? (
        <div className="flex flex-col h-full overflow-auto">
          {/* Item details */}
          <Group className="bg-gray-50 p-3 rounded-md">
            {/* Item icon */}
            <div className="w-12 h-12 flex-shrink-0">
              <Image
                src={getItemIconUrl(selectedItem.icon_file_name)}
                alt={selectedItem.value}
                fallbackSrc="/icons/fallback.jpg"
                fit="contain"
                className="rounded"
              />
            </div>
            
            {/* Item name with type icon */}
            <div className="flex-grow">
              <div className="flex items-center gap-2">
                <Text fw={700} size="md">
                  {selectedItem.value}
                </Text>
                {getItemTypeIcon(getItemType(selectedItem))}
              </div>
              
              <Text size="xs" c="dimmed" mt={4}>
                ID: {selectedItem.id} | Type: {getItemTypeName(getItemType(selectedItem))}
              </Text>
            </div>
          </Group>
          
          <Divider my="sm" />
          
          {/* Form container - make scrollable */}
          <ScrollArea className="flex-grow mb-4">
            <div className="pr-2">
              {/* Common fields for all items */}
              <Group grow align="flex-start" mb="md">
                {/* Quantity input */}
                <Tooltip 
                  label="Quantity of items to give/remove" 
                  position="top"
                  withArrow
                >
                  <Box>
                    <Text size="sm" mb={5} fw={500}>
                      Quantity <Text span color="red" component="span">*</Text>
                    </Text>
                    <NumberInput
                      value={quantity}
                      onChange={(value) => setQuantity(Number(value))}
                      min={1}
                      stepHoldDelay={500}
                      stepHoldInterval={100}
                      hideControls={false}
                      required
                      size="sm"
                      placeholder="1-100"
                    />
                  </Box>
                </Tooltip>
                
                {/* Level input */}
                <Tooltip 
                  label="Item level" 
                  position="top"
                  withArrow
                >
                  <Box>
                    <Text size="sm" mb={5} fw={500}>
                      Level
                    </Text>
                    <NumberInput
                      value={level}
                      onChange={(value) => setLevel(Number(value))}
                      min={1}
                      max={999}
                      stepHoldDelay={500}
                      stepHoldInterval={100}
                      hideControls={false}
                      size="sm"
                      placeholder="1-999"
                    />
                  </Box>
                </Tooltip>
              </Group>
              
              {/* Type-specific fields */}
              {['ARMOR', 'WEAPON', 'SKILL_CARD'].includes(getItemType(selectedItem)) && (
                <Group grow mb="md">
                  <Tooltip 
                    label="Enchantment level for weapons, armor, and skill cards" 
                    position="top"
                    withArrow
                  >
                    <Box>
                      <Text size="sm" mb={5} fw={500}>
                        Enchant
                      </Text>
                      <NumberInput
                        value={enchant}
                        onChange={(value) => setEnchant(Number(value))}
                        min={-1}
                        stepHoldDelay={500}
                        stepHoldInterval={100}
                        hideControls={false}
                        size="sm"
                        placeholder="-1 to 20"
                      />
                      <Text size="xs" c="dimmed" mt={5}>
                        -1 for default, 0-20 for specific enchant
                      </Text>
                    </Box>
                  </Tooltip>
                </Group>
              )}
              
              {/* Pet card specific field */}
              {getItemType(selectedItem) === 'PET_CARD' && (
                <Group grow mb="md">
                  <Box>
                    <Text size="sm" mb={5} fw={500}>
                      Tamed
                    </Text>
                    <Checkbox
                      checked={tamed}
                      onChange={(e) => setTamed(e.currentTarget.checked)}
                      label="Pet is tamed"
                    />
                  </Box>
                </Group>
              )}
              
              {/* Socket fields - render only if item has sockets */}
              {renderGemSelections()}
              
              {/* Selected character info */}
              {selectedCharacter && (
                <Group 
                  className="bg-blue-50 p-2 rounded-md mb-4"
                  style={{ marginTop: '10px', marginBottom: '10px' }} 
                  wrap="nowrap" 
                  align="center"
                >
                  <IconInfoCircle size={16} className="text-blue-500" />
                  <Text size="xs" className="flex-grow">
                    Will be applied to: <Text span fw={600}>{selectedCharacter.name}</Text>
                  </Text>
                </Group>
              )}
            </div>
          </ScrollArea>
          
          {/* Error alert */}
          {error && (
            <Alert 
              icon={<IconAlertCircle size={16} />} 
              title="Error" 
              color="red" 
              variant="light"
              withCloseButton
              onClose={() => setError(null)}
              mb="md"
            >
              {error}
            </Alert>
          )}
          
          {/* Action buttons and status indicators at the bottom */}
          <div className="mt-auto" style={{ marginTop: '8px' }}>
            {/* Status indicators */}
            {!selectedCharacter && (
              <Alert 
                icon={<IconInfoCircle size={16} />} 
                color="blue" 
                variant="light"
                p="xs"
                mb="md"
              >
                Select a character to give/remove items
              </Alert>
            )}
            
            {!serverConnected && (
              <Alert 
                icon={<IconAlertCircle size={16} />} 
                color="red" 
                variant="light"
                p="xs"
                mb="md"
              >
                Server is offline
              </Alert>
            )}
            
            {/* Action buttons */}
            <Group>
              <Button 
                color="green"
                onClick={handleAddItem} 
                disabled={isButtonsDisabled}
                loading={isSubmitting}
                fullWidth
                leftSection={<IconCircleCheck size={16} />}
                className={!isButtonsDisabled ? "shadow-sm hover:shadow-md transition-shadow" : ""}
              >
                Give
              </Button>
              <Button 
                color="red" 
                onClick={handleRemoveItem} 
                disabled={isButtonsDisabled}
                loading={isSubmitting}
                fullWidth
                leftSection={<IconCircleX size={16} />}
                className={!isButtonsDisabled ? "shadow-sm hover:shadow-md transition-shadow" : ""}
              >
                Remove
              </Button>
            </Group>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          <Text c="dimmed" ta="center">
            Select an item to view properties
          </Text>
        </div>
      )}
    </Paper>
  );
};

export default ItemProperties; 